import { KennoFile } from '@pandiafi/design-system'
import { Field, FieldProps, useFormikContext } from 'formik'
import React, { useCallback } from 'react'

import FormFieldWrapper from 'components/FormField'
import { UploadFiles } from 'components/UploadFiles'

interface FilesFieldProps<
  TValues extends Record<string, unknown> = Record<string, unknown>,
> {
  name: keyof TValues & string
  label: string
  className?: string
  isUploading?: (value: boolean) => void
}

export const FilesField = <
  TValues extends Record<string, unknown> = Record<string, unknown>,
>({
  name,
  label,
  className,
  isUploading,
}: FilesFieldProps<TValues>) => {
  const form = useFormikContext<TValues>()

  const handleOnFilesUploaded = useCallback(
    (newFiles: KennoFile[]) => {
      const currentFiles = (form.values[name] || []) as KennoFile[]
      form.setFieldValue(name, [...currentFiles, ...newFiles])
    },
    [form, name]
  )

  const handleOnDeleteUploadedFile = useCallback(
    (fileToRemove: KennoFile) => {
      const currentFiles = (form.values[name] || []) as KennoFile[]
      const updated = currentFiles.filter((f) => f.uuid !== fileToRemove.uuid)
      form.setFieldValue(name, updated)
    },
    [form, name]
  )

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps<KennoFile[]>) => (
        <FormFieldWrapper
          name={name}
          label={label}
          className={className}
          meta={meta}
          labelBold
        >
          <UploadFiles
            id={name}
            files={field.value || []}
            onFilesUploaded={handleOnFilesUploaded}
            onDeleteFile={handleOnDeleteUploadedFile}
            onUploadOngoing={isUploading}
          />
        </FormFieldWrapper>
      )}
    </Field>
  )
}
